import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost } from '../lib/api'
import {
  X,
  Search,
  Plus,
  AlertCircle,
} from 'lucide-react'

interface Profession {
  id: string
  name: string
}

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
]

interface AddProfessionModalProps {
  isOpen: boolean
  onClose: () => void
}

export default function AddProfessionModal({ isOpen, onClose }: AddProfessionModalProps) {
  const queryClient = useQueryClient()
  const [professionSearch, setProfessionSearch] = useState('')
  const [selectedProfession, setSelectedProfession] = useState<Profession | null>(null)
  const [selectedState, setSelectedState] = useState('')
  const [error, setError] = useState('')
  const [isRequestingNew, setIsRequestingNew] = useState(false)
  const [newProfessionName, setNewProfessionName] = useState('')

  const { data: allProfessionsData } = useQuery({
    queryKey: ['/api/professions'],
    queryFn: () => apiGet<Profession[]>('/professions'),
    enabled: isOpen,
  })

  const addProfessionMutation = useMutation({
    mutationFn: (data: { professionId: string; state: string }) =>
      apiPost('/user/professions', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user/professions'] })
      queryClient.invalidateQueries({ queryKey: ['/api/user/ce-progress-summary'] })
      handleClose()
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to add renewal')
    },
  })

  const requestProfessionMutation = useMutation({
    mutationFn: (data: { professionName: string; state: string }) =>
      apiPost('/professions/request', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user/professions'] })
      queryClient.invalidateQueries({ queryKey: ['/api/user/ce-progress-summary'] })
      handleClose()
    },
    onError: (err: Error & { message?: string }) => {
      if (err.message?.includes('already have')) {
        setError('You already have this renewal and state combination.')
      } else {
        setError(err.message || 'Failed to add renewal')
      }
    },
  })

  const allProfessions = allProfessionsData || []
  const filteredProfessions = professionSearch.length > 1
    ? allProfessions.filter(p =>
        p.name.toLowerCase().includes(professionSearch.toLowerCase())
      )
    : []

  const handleClose = () => {
    setProfessionSearch('')
    setSelectedProfession(null)
    setSelectedState('')
    setError('')
    setIsRequestingNew(false)
    setNewProfessionName('')
    onClose()
  }

  const handleAddProfession = () => {
    if (!selectedProfession || !selectedState) {
      setError('Please select both a renewal and state')
      return
    }
    addProfessionMutation.mutate({
      professionId: selectedProfession.id,
      state: selectedState,
    })
  }

  const handleRequestNewProfession = () => {
    if (!newProfessionName.trim() || !selectedState) {
      setError('Please enter a renewal name and select a state')
      return
    }
    requestProfessionMutation.mutate({
      professionName: newProfessionName.trim(),
      state: selectedState,
    })
  }

  const startRequestingNew = () => {
    setIsRequestingNew(true)
    setNewProfessionName(professionSearch)
    setProfessionSearch('')
    setError('')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Add Renewal</h3>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          {isRequestingNew ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Renewal Name
                </label>
                <input
                  type="text"
                  value={newProfessionName}
                  onChange={(e) => setNewProfessionName(e.target.value)}
                  placeholder="e.g., Registered Nurse, Physical Therapist..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter the full name of your renewal
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select state...</option>
                  {US_STATES.map((state) => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setIsRequestingNew(false)
                    setNewProfessionName('')
                    setError('')
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={handleRequestNewProfession}
                  disabled={!newProfessionName.trim() || !selectedState || requestProfessionMutation.isPending}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                >
                  {requestProfessionMutation.isPending ? 'Adding...' : 'Add Renewal'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Renewal
                </label>
                {selectedProfession ? (
                  <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <span className="font-medium text-blue-700">{selectedProfession.name}</span>
                    <button
                      onClick={() => setSelectedProfession(null)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={professionSearch}
                      onChange={(e) => setProfessionSearch(e.target.value)}
                      placeholder="Search renewals (e.g., RN, EMT, Paramedic)..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {filteredProfessions.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-10">
                        {filteredProfessions.map((prof) => (
                          <button
                            key={prof.id}
                            onClick={() => {
                              setSelectedProfession(prof)
                              setProfessionSearch('')
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-gray-50"
                          >
                            {prof.name}
                          </button>
                        ))}
                      </div>
                    )}
                    {professionSearch.length > 1 && filteredProfessions.length === 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-10">
                        <p className="text-sm text-gray-500 mb-3">No renewals found matching "{professionSearch}"</p>
                        <button
                          onClick={startRequestingNew}
                          className="w-full px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors inline-flex items-center justify-center gap-2"
                        >
                          <Plus className="w-4 h-4" />
                          Add "{professionSearch}" as a new renewal
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select state...</option>
                  {US_STATES.map((state) => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddProfession}
                  disabled={!selectedProfession || !selectedState || addProfessionMutation.isPending}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addProfessionMutation.isPending ? 'Adding...' : 'Add Renewal'}
                </button>
              </div>

              <div className="text-center pt-2 border-t border-gray-100">
                <button
                  onClick={startRequestingNew}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Can't find your renewal? Request a new one
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
